/**
 * Words for the email draft panel's offer email (SA-1040 PR 2, spec §5.2, §6). Pure: the panel
 * hands in what the draft read served and renders the text.
 */
import { ApiError } from '@/services/apiClient'
import { formatCurrency } from '@/utils/currency'
import type { EmailDraftQuote } from '@/services/emailDrafts'

export const AI_HEADING = 'AI email draft — review before sending'
export const OFFER_HEADING = 'Offer email — review before sending'
export const DOWNLOAD = 'Download PDF'
export const REWORDED = 'The quotation was edited, so this email was reworded. What you had typed here was replaced.'
export const signedAs = (name: string) => `Signed as ${name}`
export const DOWNLOAD_FAILED = 'The quotation could not be downloaded.'

/** "84 KB" / "3.0 MB"; a file under a kilobyte reads 1 KB. */
export function sizeText(bytes: number): string {
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

/** "Quotation Q-0142 v2 · $485,000 USD · PDF, 84 KB attached": the amount sits beside a person's
 *  own text, which an edit of the version keeps (plan D-g). */
export function attachmentLine(q: EmailDraftQuote): string {
  const file = q.document_byte_size != null ? `PDF, ${sizeText(q.document_byte_size)} attached` : 'PDF attached'
  return `Quotation ${q.order_number} v${q.version_number} · ${formatCurrency(q.amount_cents, q.currency)} · ${file}`
}

/** A refused offer send, by its code (`outbound.OFFER_CODES`). */
export const SEND_REFUSALS: Record<string, string> = {
  quote_not_prepared: 'This quotation is no longer prepared: it was sent or replaced. Open the record to see where it stands.',
  record_closed: 'This record is closed.',
  quote_changed: 'The quotation was edited after this draft was opened. Check the draft again, then send.',
  quote_validity_passed: 'The quotation’s validity has passed. Edit the validity on the record, then send.',
  quote_not_awaited: 'The record’s process no longer waits for a quote. Open the record to see where it stands.',
  quote_document_missing: 'The quotation file is missing. Edit the quote on the record to render it again.',
}

/** A quote refusal's words; any other error reads as it always did. */
export function sendErrorText(err: unknown): string {
  if (err instanceof ApiError && typeof err.detail === 'string' && SEND_REFUSALS[err.detail]) return SEND_REFUSALS[err.detail]
  return err instanceof Error ? err.message : String(err)
}
